import { useState, useEffect } from "react";
import { Loader2, RefreshCw, ChevronDown, ChevronRight, CheckCircle2, XCircle, Clock } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const StatusBadge = ({ status }) => {
  const map = {
    completed: { cls: "text-emerald-400 border-emerald-500/40 bg-emerald-500/10", Icon: CheckCircle2 },
    failed: { cls: "text-red-400 border-red-500/40 bg-red-500/10", Icon: XCircle },
    queued: { cls: "text-amber-400 border-amber-500/40 bg-amber-500/10", Icon: Clock },
    generating: { cls: "text-sky-400 border-sky-500/40 bg-sky-500/10", Icon: Clock },
  };
  const { cls, Icon } = map[status] || map.queued;
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] font-heading font-bold uppercase tracking-wide rounded-full border px-2 py-0.5 ${cls}`}>
      <Icon size={11} /> {status}
    </span>
  );
};

function Row({ g }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-white/10 rounded-lg bg-ink-900/60 overflow-hidden" data-testid={`gen-log-row-${g.id}`}>
      <button onClick={() => setOpen((o) => !o)} data-testid={`gen-log-toggle-${g.id}`}
        className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-white/5 transition-colors">
        {open ? <ChevronDown size={15} className="text-zinc-500 shrink-0" /> : <ChevronRight size={15} className="text-zinc-500 shrink-0" />}
        <StatusBadge status={g.status} />
        <span className="text-xs text-zinc-400 shrink-0">{g.model}</span>
        <span className="text-[10px] text-zinc-600 shrink-0 hidden sm:inline">{g.generation_type}</span>
        <span className="text-xs text-zinc-300 truncate flex-1">{g.kind || g.template_title || (g.prompt || "").slice(0, 60)}</span>
        {g.status === "failed" && g.failed_reason && (
          <span className="text-[10px] text-red-400 truncate max-w-[40%] hidden md:inline" data-testid={`gen-log-reason-${g.id}`}>{g.failed_reason}</span>
        )}
        <span className="text-[10px] text-zinc-600 shrink-0 hidden lg:inline">{new Date(g.created_at).toLocaleString()}</span>
      </button>
      {open && (
        <div className="px-4 pb-4 pt-1 space-y-2 text-xs border-t border-white/5">
          <Field label="Video ID" value={g.id} />
          <Field label="Task ID" value={g.task_id || "—"} />
          <Field label="User" value={g.user_email || g.user_id} />
          <Field label="Cost / Refunded" value={`${g.cost} credits · ${g.refunded ? "refunded" : "charged/pending"}`} />
          <Field label="Duration / Resolution" value={`${g.duration}s · ${g.resolution} · ${g.aspect_ratio}`} />
          {g.status === "failed" && (
            <div className="rounded-md border border-red-500/30 bg-red-500/5 p-2">
              <div className="label-mono text-red-400 mb-1">Failed Reason (from Seedance)</div>
              <div className="text-red-300 break-words" data-testid={`gen-log-failreason-${g.id}`}>{g.failed_reason || "Unknown"}</div>
            </div>
          )}
          {g.video_url && <Field label="Result URL" value={g.video_url} mono />}
          <Json label="Request sent to Seedance" data={g.seedance_request} />
          <Json label="Seedance create response" data={g.seedance_response} />
          {g.seedance_webhook_response && <Json label="Webhook response" data={g.seedance_webhook_response} />}
          {g.seedance_status_response && <Json label="Status poll response" data={g.seedance_status_response} />}
        </div>
      )}
    </div>
  );
}

const Field = ({ label, value, mono }) => (
  <div className="flex flex-col sm:flex-row sm:items-baseline gap-0.5 sm:gap-2">
    <span className="label-mono text-zinc-500 shrink-0 sm:w-44">{label}</span>
    <span className={`text-zinc-300 break-all ${mono ? "font-mono text-[11px]" : ""}`}>{value}</span>
  </div>
);

const Json = ({ label, data }) => {
  if (!data) return null;
  return (
    <div>
      <div className="label-mono text-zinc-500 mb-1">{label}</div>
      <pre className="bg-ink-950 border border-white/10 rounded-md p-2 overflow-x-auto text-[10px] text-zinc-400 whitespace-pre-wrap break-all">{JSON.stringify(data, null, 2)}</pre>
    </div>
  );
};

export default function GenerationLogAdmin() {
  const [gens, setGens] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("all");

  const load = () => {
    setLoading(true);
    api.get("/admin/generations?limit=150")
      .then(({ data }) => setGens(data.generations || []))
      .catch((e) => toast.error(e.response?.data?.detail || "Failed to load log"))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const shown = (gens || []).filter((g) => filter === "all" || g.status === filter);
  const counts = (gens || []).reduce((a, g) => { a[g.status] = (a[g.status] || 0) + 1; return a; }, {});

  return (
    <div className="space-y-4" data-testid="admin-generation-log">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h2 className="label-mono flex items-center gap-3"><span className="w-8 h-px bg-gold" /> Generation Log</h2>
        <button onClick={load} disabled={loading} data-testid="gen-log-refresh"
          className="flex items-center gap-1.5 text-xs text-gold border border-gold/40 rounded px-3 py-1.5 hover:bg-[#1a140d] transition-colors disabled:opacity-60">
          {loading ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />} Refresh
        </button>
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        {["all", "completed", "failed", "queued", "generating"].map((f) => (
          <button key={f} onClick={() => setFilter(f)} data-testid={`gen-log-filter-${f}`}
            className={`text-xs font-heading rounded-full px-3 py-1 border transition-colors ${filter === f ? "bg-gold text-ink-950 border-gold" : "border-white/10 text-zinc-400 hover:border-gold/50"}`}>
            {f}{f !== "all" && counts[f] ? ` (${counts[f]})` : ""}
          </button>
        ))}
      </div>
      {gens === null ? (
        <div className="flex justify-center py-16"><Loader2 className="animate-spin text-gold" size={26} /></div>
      ) : shown.length === 0 ? (
        <p className="text-sm text-zinc-600 py-8 text-center">No generations{filter !== "all" ? ` with status "${filter}"` : ""} yet.</p>
      ) : (
        <div className="space-y-2">{shown.map((g) => <Row key={g.id} g={g} />)}</div>
      )}
    </div>
  );
}
